import { server } from '@/constants';

import { onUnauthorized } from './unauthorized-handler';

export interface ApiClientOptions extends RequestInit {
  /** Custom base URL (defaults to server constant) */
  baseUrl?: string;
  /** Skip default headers (useful for FormData uploads where browser sets Content-Type) */
  skipDefaultHeaders?: boolean;
  /** Query parameters to append to the URL */
  query?: Record<string, string | number | boolean | undefined | null>;
}

export interface ApiError extends Error {
  status: number;
  statusText: string;
  data?: unknown;
}

/**
 * Recursively searches for error messages in nested objects
 */
function findErrorMessage(data: unknown, seen = new WeakSet<object>(), depth = 0, maxDepth = 10): string | null {
  // Avoid infinite recursion
  if (depth > maxDepth) {
    return null;
  }

  // Return primitives as-is
  if (data === null || data === undefined) {
    return null;
  }

  if (typeof data !== 'object') {
    if (typeof data === 'string') {
      return data;
    }
    return null;
  }

  // Handle circular references
  if (seen.has(data as object)) {
    return null;
  }
  seen.add(data as object);

  // Handle arrays - search first element
  if (Array.isArray(data)) {
    if (data.length > 0) {
      return findErrorMessage(data[0], seen, depth + 1, maxDepth);
    }
    return null;
  }

  // Handle objects - check ALL fields for common error message fields first,
  // before recursing into nested objects
  const errorObj = data as Record<string, unknown>;
  const searchOrder = ['message', 'detail', 'error', 'msg', 'err'];

  for (const field of searchOrder) {
    if (typeof errorObj[field] === 'string') {
      return errorObj[field] as string;
    }
  }

  // Check all fields that contain string values (but aren't the common error fields)
  // This catches cases like { session: "expired" }
  for (const [key, value] of Object.entries(errorObj)) {
    if (!searchOrder.includes(key) && typeof value === 'string') {
      return value;
    }
  }

  // If no string value found, recursively search all object/array values
  for (const value of Object.values(errorObj)) {
    if (typeof value === 'object' && value !== null) {
      const foundMessage = findErrorMessage(value, seen, depth + 1, maxDepth);
      if (foundMessage) {
        return foundMessage;
      }
    }
  }

  return null;
}

/**
 * Creates an API error with additional context
 */
function createApiError(response: Response, data?: unknown): ApiError {
  // Extract error message from response data if available
  let errorMessage = response.statusText;

  if (data) {
    const foundMessage = findErrorMessage(data);
    if (foundMessage) {
      errorMessage = foundMessage;
    } else if (typeof data === 'string') {
      errorMessage = data;
    }
  }

  const error = new Error(errorMessage) as ApiError;
  error.status = response.status;
  error.statusText = response.statusText;
  error.data = data;
  return error;
}

/**
 * List of sensitive field names that should be redacted from logs
 */
const SENSITIVE_FIELDS = [
  'password',
  'token',
  'access_token',
  'refresh_token',
  'api_key',
  'apiKey',
  'secret',
  'authorization',
  'auth',
  'session',
  'sessionId',
  'csrf',
  'cookie',
  'credentials',
] as const;

/**
 * Sanitizes error data for logging by removing sensitive fields
 *
 * @param errorData - The error data to sanitize
 * @returns Sanitized error data safe for logging
 * This function recursively traverses error objects and redacts any fields
 * that might contain sensitive information like tokens, passwords, or session data.
 *
 * @example
 * ```typescript
 * const error = { message: 'Auth failed', token: 'secret123' };
 * const sanitized = sanitizeErrorForLogging(error);
 * // Returns: { message: 'Auth failed', token: '[REDACTED]' }
 * ```
 */
function sanitizeErrorForLogging(errorData: unknown, seen = new WeakSet()): unknown {
  // Return primitives as-is
  if (errorData === null || errorData === undefined) {
    return errorData;
  }

  if (typeof errorData !== 'object') {
    return errorData;
  }

  // Handle circular references
  if (seen.has(errorData as object)) {
    return '[Circular]';
  }
  seen.add(errorData as object);

  // Handle arrays
  if (Array.isArray(errorData)) {
    return errorData.map(item => sanitizeErrorForLogging(item, seen));
  }

  // Handle objects
  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(errorData)) {
    const lowerKey = key.toLowerCase();

    // Check if this field should be redacted
    const isSensitive = SENSITIVE_FIELDS.some(field => lowerKey.includes(field.toLowerCase()));

    if (isSensitive) {
      sanitized[key] = '[REDACTED]';
    } else if (value && typeof value === 'object') {
      // Recursively sanitize nested objects
      sanitized[key] = sanitizeErrorForLogging(value, seen);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

/**
 * Authenticated API client that checks auth status before requests
 * and automatically handles 401 responses
 */
export class UnauthorizedError extends Error {
  public isUnauthorized: boolean;
  constructor(message: string) {
    super(message);
    this.name = 'UnauthorizedError';
    this.isUnauthorized = true;
  }
}

class ApiClient {
  private static instance: ApiClient;

  private constructor() {}

  static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  /**
   * Handle 401 Unauthorized responses
   */
  private handleUnauthorizedResponse(): void {
    onUnauthorized();
  }

  /**
   * Makes an authenticated fetch request
   *
   * @param url - The URL to fetch (relative to baseUrl or absolute)
   * @param options - Fetch options with additional ApiClientOptions
   * @returns Promise resolving to the parsed JSON response, or null for 204 No Content
   *
   * @throws {ApiError} When the response is not ok
   *   * Note: This method returns `T | null` because 204 No Content responses return null.
   * If you need to ensure non-null responses, use the typed convenience methods or
   * check the result for null values.
   *
   * @example
   * ```typescript
   * // GET request
   * const data = await apiClient.fetch('/orgs/123/studies');
   *
   * // POST request
   * const result = await apiClient.fetch('/orgs/123/studies', {
   *   method: 'POST',
   *   body: JSON.stringify({ name: 'New Study' })
   * });
   *
   * // DELETE request (may return null for 204 No Content)
   * const result = await apiClient.fetch('/orgs/123/studies/456', {
   *   method: 'DELETE'
   * });
   * if (result === null) {
   *   console.log('Deleted successfully');
   * }
   * ```
   */
  async fetch<T = unknown>(url: string, options: ApiClientOptions = {}): Promise<T | null> {
    const { baseUrl = server, headers = {}, skipDefaultHeaders = false, ...fetchOptions } = options;

    // Construct full URL
    let fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
    // Append query params if present
    if (options.query && Object.keys(options.query).length > 0) {
      const searchParams = new URLSearchParams();
      Object.entries(options.query).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      fullUrl += (fullUrl.includes('?') ? '&' : '?') + searchParams.toString();
    }

    // Merge default headers with provided headers
    // Skip default headers for FormData uploads where browser needs to set Content-Type with boundary
    const isFormData = fetchOptions.body instanceof FormData;
    const finalHeaders: HeadersInit =
      skipDefaultHeaders || isFormData
        ? headers
        : {
            'Content-Type': 'application/json',
            ...headers,
          };

    // Log outgoing request details (with sensitive data redacted) only in DEV
    if (import.meta.env.DEV) {
      const sanitizedBody = fetchOptions.body
        ? sanitizeErrorForLogging(
            typeof fetchOptions.body === 'string'
              ? (() => {
                  try {
                    return JSON.parse(fetchOptions.body as string);
                  } catch {
                    return fetchOptions.body;
                  }
                })()
              : fetchOptions.body,
          )
        : undefined;

      console.info('[ApiClient] Request:', {
        url: fullUrl,
        method: fetchOptions.method || 'GET',
        headers: sanitizeErrorForLogging(finalHeaders),
        body: sanitizedBody,
      });
    }

    // Make the fetch request with credentials included
    const response = await fetch(fullUrl, {
      credentials: 'include',
      mode: 'cors',
      headers: finalHeaders,
      ...fetchOptions,
    });

    // Log response status and headers
    if (import.meta.env.DEV) {
      const headerEntries = response.headers?.entries ? Object.fromEntries(response.headers.entries()) : {};
      console.info('[ApiClient] Response:', {
        url: fullUrl,
        status: response.status,
        statusText: response.statusText,
        headers: headerEntries,
      });
    }

    // Handle 401 Unauthorized
    if (response.status === 401) {
      this.handleUnauthorizedResponse();
      throw createApiError(response);
    }

    // Handle other error responses
    if (!response.ok) {
      let errorData: unknown;
      try {
        errorData = await response.json();
      } catch {
        // Response body is not JSON or empty
        errorData = await response.text();
      }

      // Handle 403 Unauthorized
      if (response.status === 403) {
        throw createApiError(response, errorData);
      }

      const apiError = createApiError(response, errorData);

      // Log detailed error information (with sensitive data redacted)
      console.error('[ApiClient] API request failed:', {
        url: fullUrl,
        status: response.status,
        statusText: response.statusText,
        error: sanitizeErrorForLogging(errorData),
      });

      throw apiError;
    }

    // Parse successful response
    try {
      // Check if response has content
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }

      // Return null for 204 No Content
      if (response.status === 204) {
        return null;
      }

      // Try to parse as JSON, throw error if it fails
      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch {
        // If we get here, the response was successful but not JSON-parseable
        // This indicates an unexpected response format from the API
        console.error('[ApiClient] Received non-JSON response:', {
          url: fullUrl,
          status: response.status,
          contentType,
          text: text.slice(0, 200), // Log first 200 chars
        });
        throw new Error(
          `Unexpected response format: received non-JSON response from ${fullUrl}. ` +
            `Status: ${response.status}, Content-Type: ${contentType || 'none'}`,
        );
      }
    } catch (error) {
      console.error('[ApiClient] Failed to parse response:', error);
      throw error;
    }
  }

  /**
   * Convenience method for GET requests
   *   * Note: Returns T | null because 204 No Content responses return null.
   */
  async get<T = unknown>(url: string, options?: Omit<ApiClientOptions, 'method' | 'body'>): Promise<T | null> {
    return this.fetch<T>(url, { ...options, method: 'GET' });
  }

  /**
   * Convenience method for POST requests
   *   * Note: Returns T | null because 204 No Content responses return null.
   */
  async post<T = unknown>(
    url: string,
    body?: unknown,
    options?: Omit<ApiClientOptions, 'method' | 'body'>,
  ): Promise<T | null> {
    return this.fetch<T>(url, {
      ...options,
      method: 'POST',
      body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * Convenience method for PUT requests
   *   * Note: Returns T | null because 204 No Content responses return null.
   */
  async put<T = unknown>(
    url: string,
    body?: unknown,
    options?: Omit<ApiClientOptions, 'method' | 'body'>,
  ): Promise<T | null> {
    return this.fetch<T>(url, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * Convenience method for PATCH requests
   *   * Note: Returns T | null because 204 No Content responses return null.
   */
  async patch<T = unknown>(
    url: string,
    body?: unknown,
    options?: Omit<ApiClientOptions, 'method' | 'body'>,
  ): Promise<T | null> {
    return this.fetch<T>(url, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * Convenience method for DELETE requests
   *   * DELETE requests typically return 204 No Content, so this will usually return null.
   * If you need to handle the null case explicitly, check the return value:
   *
   * @example
   * ```typescript
   * const result = await apiClient.delete('/orgs/123/studies/456');
   * if (result === null) {
   *   console.log('Deleted successfully (204 No Content)');
   * }
   * ```
   */
  /**
   * Convenience method for DELETE requests
   *   * DELETE requests typically return 204 No Content, so this will usually return null.
   * If you need to handle the null case explicitly, check the return value:
   *
   * @example
   * ```typescript
   * const result = await apiClient.delete('/orgs/123/studies/456');
   * if (result === null) {
   *   console.log('Deleted successfully (204 No Content)');
   * }
   * ```
   *
   * @param url - The URL to send the DELETE request to.
   * @param bodyOrOptions - If an object (but not a RequestInit, Headers, FormData, or URLSearchParams), treated as the request body.
   *                        If an ApiClientOptions object, treated as options.
   *                        If a primitive or unexpected type, an error will be thrown.
   * @param options - Additional options to merge in if bodyOrOptions is a body.
   */
  async delete<T = unknown>(
    url: string,
    bodyOrOptions?: unknown,
    options?: Omit<ApiClientOptions, 'method' | 'body'>,
  ): Promise<T | null> {
    let fetchOptions: ApiClientOptions = { method: 'DELETE' };

    if (bodyOrOptions === undefined) {
      // No body or options
      // fetchOptions is just { method: 'DELETE' }
    } else if (
      typeof bodyOrOptions === 'object' &&
      bodyOrOptions !== null &&
      !(bodyOrOptions instanceof Headers) &&
      !(bodyOrOptions instanceof URLSearchParams) &&
      !(bodyOrOptions instanceof FormData)
    ) {
      // Could be body or options, check for known ApiClientOptions keys
      const maybeOptions = bodyOrOptions as Partial<ApiClientOptions>;
      const isProbablyOptions =
        'baseUrl' in maybeOptions ||
        'skipDefaultHeaders' in maybeOptions ||
        'query' in maybeOptions ||
        'headers' in maybeOptions ||
        'credentials' in maybeOptions ||
        'mode' in maybeOptions ||
        'cache' in maybeOptions ||
        'redirect' in maybeOptions ||
        'referrer' in maybeOptions ||
        'integrity' in maybeOptions ||
        'keepalive' in maybeOptions ||
        'signal' in maybeOptions;

      if (isProbablyOptions) {
        fetchOptions = {
          ...fetchOptions,
          ...(bodyOrOptions as ApiClientOptions),
        };
      } else {
        // Treat as body
        fetchOptions.body = JSON.stringify(bodyOrOptions);
        if (options) {
          fetchOptions = { ...fetchOptions, ...options };
        }
      }
    } else if (
      typeof bodyOrOptions === 'string' ||
      typeof bodyOrOptions === 'number' ||
      typeof bodyOrOptions === 'boolean'
    ) {
      // Primitives are not valid for body or options
      throw new TypeError(
        `apiClient.delete: bodyOrOptions must be an object (body or ApiClientOptions), not a primitive (${typeof bodyOrOptions})`,
      );
    } else {
      // Fallback: treat as options if possible, otherwise throw
      if (bodyOrOptions) {
        fetchOptions = {
          ...fetchOptions,
          ...(bodyOrOptions as ApiClientOptions),
        };
      }
    }

    return this.fetch<T>(url, fetchOptions);
  }
}

// Export singleton instance
export const apiClient = ApiClient.getInstance();

// Export type for the client
export type { ApiClient };
